import { readStoredAccessToken } from '../../shared/service/requestCredential';
import { browserObservabilityConfig } from './config';

type BrowserSignal = 'lcp' | 'cls' | 'inp' | 'navigation' | 'api_duration' | 'javascript_error' | 'chunk_load_error';
type Budget = 'core_vital' | 'navigation' | 'api' | 'error';
interface BrowserEvent { signal: BrowserSignal; value: number; frontend_version: string; }

const BUDGET_LIMIT: Record<Budget, number> = {
  core_vital: 9,
  navigation: 1,
  api: 5,
  error: 5,
};
let budgetUsed: Record<Budget, number> = { core_vital: 0, navigation: 0, api: 0, error: 0 };
// Listeners globais e o PerformanceObserver só podem ser instalados uma vez:
// uma segunda chamada duplicaria cada amostra e daria ao page load um
// orçamento novo. index.tsx chama dentro de StrictMode, que reexecuta efeitos.
let teardown: Array<() => void> | undefined;
// O receptor aceita lote fechado. Sem agregar, um page load amostrado geraria
// até 20 POSTs — e cada um custa uma autenticação e um SELECT de User no
// backend que esta telemetria deveria medir, não produzir.
let buffer: BrowserEvent[] = [];

function selectedForSample(rate: number): boolean {
  if (rate <= 0) return false;
  if (rate >= 1) return true;
  const key = 'mestti:browser-observability-sample';
  try {
    const stored = sessionStorage.getItem(key);
    if (stored !== null) return stored === '1';
    const selected = crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32 < rate;
    sessionStorage.setItem(key, selected ? '1' : '0');
    return selected;
  } catch {
    return false;
  }
}

function accessToken(): string | null {
  try { return readStoredAccessToken(); } catch { return null; }
}

function send(signal: BrowserSignal, value: number, budget: Budget) {
  if (!Number.isFinite(value) || value < 0 || budgetUsed[budget] >= BUDGET_LIMIT[budget]) return;
  if (!accessToken()) return;
  budgetUsed[budget] += 1;
  buffer.push({
    signal,
    value: Math.round(value * 1000) / 1000,
    frontend_version: browserObservabilityConfig.frontendVersion,
  });
  if (buffer.length >= browserObservabilityConfig.maxBatch) flush();
}

function flush() {
  if (buffer.length === 0) return;
  const token = accessToken();
  if (!token) { buffer = []; return; }
  const events = buffer;
  buffer = [];
  try {
    void fetch(
      browserObservabilityConfig.ingestionUrl,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
      },
    ).catch(() => undefined);
  } catch {
    // Telemetria é best-effort e nunca altera o fluxo da aplicação.
  }
}

function observeApiDurations() {
  try {
    const observer: PerformanceObserver = new PerformanceObserver((list) => list.getEntries().forEach((entry) => {
      const resource = entry as PerformanceResourceTiming;
      if (!['fetch', 'xmlhttprequest'].includes(resource.initiatorType)) return;
      try {
        const url = new URL(resource.name);
        // Comparar com window.location.origin descartava TODA chamada real de
        // API, porque a API vive em outra origem que a SPA em produção.
        if (url.origin === browserObservabilityConfig.apiOrigin
            && url.pathname.startsWith('/api/')
            && url.href !== browserObservabilityConfig.ingestionUrl) {
          send('api_duration', resource.duration, 'api');
        }
      } catch { /* URL inválida é ignorada. */ }
    }));
    observer.observe({ type: 'resource', buffered: true });
    teardown?.push(() => observer.disconnect());
  } catch {
    // Resource Timing indisponível não afeta os Core Web Vitals.
  }
}

function observeCoreWebVitals() {
  import('web-vitals').then(({ onCLS, onINP, onLCP }) => {
    onCLS(({ value }) => send('cls', value, 'core_vital'));
    onINP(({ value }) => send('inp', value, 'core_vital'));
    onLCP(({ value }) => send('lcp', value, 'core_vital'));
  }).catch(() => undefined);
}

export function startBrowserObservability() {
  if (teardown) return;
  if (!browserObservabilityConfig.enabled
      || !selectedForSample(browserObservabilityConfig.sampleRate)) return;

  teardown = [];
  observeCoreWebVitals();
  observeApiDurations();
  try {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (navigation) send('navigation', navigation.duration, 'navigation');
  } catch { /* Navigation Timing é opcional. */ }

  // A mensagem é lida só para classificar o sinal e nunca sai do navegador.
  const onError = (event: ErrorEvent) => {
    const message = String(event.message || '');
    send(message.toLowerCase().includes('chunk') ? 'chunk_load_error' : 'javascript_error', 1, 'error');
  };
  const onRejection = () => send('javascript_error', 1, 'error');
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  teardown.push(() => window.removeEventListener('error', onError));
  teardown.push(() => window.removeEventListener('unhandledrejection', onRejection));

  // Descarga do lote. `hidden` é o único momento confiável em mobile; `pagehide`
  // cobre o descarregamento. `keepalive` é o que permite a requisição sobreviver.
  const onHidden = () => { if (document.visibilityState === 'hidden') flush(); };
  window.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', flush);
  teardown.push(() => window.removeEventListener('visibilitychange', onHidden));
  teardown.push(() => window.removeEventListener('pagehide', flush));
}

export function flushBrowserObservability() {
  flush();
}

export function stopBrowserObservability() {
  teardown?.forEach((detach) => {
    try { detach(); } catch { /* teardown é best-effort. */ }
  });
  teardown = undefined;
  buffer = [];
  budgetUsed = { core_vital: 0, navigation: 0, api: 0, error: 0 };
}
